import * as Ariakit from '@ariakit/react'
import classNames from 'classnames'
import * as React from 'react'
import { Portal } from 'reakit'

import { Input } from '../input'

enum ListRounding {
  none = '',
  regular = 'rounded-lg',
}

enum ListTheme {
  none = '',
  default = 'border border-primary bg-primary',
}

function focusWithoutScroll(element: HTMLElement | null) {
  element?.focus({ preventScroll: true })

  return !element
}

function onBackdropClick(ev: React.MouseEvent) {
  ev.preventDefault()
}

export interface SelectListProps {
  /**
   * Internal prop which enables input at the top of the popover list
   */
  isComboBox?: boolean

  /**
   * Set rounding for the popover list
   *
   * @default regular
   */
  rounding?: keyof typeof ListRounding

  /**
   * Popover theme
   */
  theme?: keyof typeof ListTheme

  /**
   * The amount of space between button and its popover
   *
   * @default 4
   */
  gutter?: number

  /**
   * Change how the items are laid out
   *
   * @default vertical
   */
  orientation?: 'vertical' | 'horizontal'

  /**
   * Custom popover class
   */
  className?: string

  /**
   * Internal prop used to display no results when automatic `item` filtering doesn’t return any items
   */
  hasItemChildren?: boolean

  /**
   * Do not render the items when the popover is not visible
   *
   * @default false
   */
  unmountOnHide?: boolean
}

export function SelectList({
  isComboBox,
  rounding = 'regular',
  theme = 'default',
  gutter = 4,
  orientation = 'vertical',
  className,
  unmountOnHide = false,
  hasItemChildren,
  children,
}: React.PropsWithChildren<SelectListProps>) {
  const ListElement = React.useMemo(
    () => (isComboBox ? Ariakit.ComboboxList : (wrapperProps: any) => <div {...wrapperProps} />),
    [isComboBox]
  )
  const context = Ariakit.useSelectContext()!
  const placement = context.useState('currentPlacement')
  const hasChildren = hasItemChildren || React.Children.count(children) > 0

  return (
    // Temporary fix for modal reakit dialogs
    <Portal>
      <Ariakit.SelectPopover
        // Render in a portal to escape overflow
        modal={false}
        preventBodyScroll={false}
        // Do not keep the children mounted when the popover is closed
        unmountOnHide={unmountOnHide}
        // Space between button and popover
        gutter={gutter}
        // Manage the focus ourselves so we can prevent scrolling when focusing the disclosure element
        autoFocusOnHide={focusWithoutScroll}
        // Custom event prevents scrolling issues
        // See website PR #1968
        backdrop={<div onMouseDown={onBackdropClick} />}
        className={classNames(
          className,
          ListRounding[rounding],
          ListTheme[theme],
          'custom-scrollbar absolute z-dialog flex flex-col overflow-hidden shadow-modal outline-none dark:shadow-modal-dark',
          // These are set by popper, we just need to make sure we don’t encroach on the safe insets
          'min-w-[--popover-anchor-width] max-w-[--popover-available-width]',
          // Animations
          'opacity-0 transition duration-[250ms] data-[enter]:translate-y-0 data-[enter]:opacity-100',
          {
            // When in combobox mode, the padding is smaller so we can render a gradient under the combobox input.
            // Change the custom scrollbar offset so the top of the first item fits the top of our scrollbar
            '[--scrollbar-offset-top:theme(spacing.1)]': isComboBox,
            '[--scrollbar-radius:0]': ListRounding[rounding] === ListRounding.none,
            // Direction based styles
            'max-h-[calc(var(--popover-available-height)-env(safe-area-inset-bottom))] -translate-y-1':
              placement.startsWith('bottom'),
            'max-h-[calc(var(--popover-available-height)-env(safe-area-inset-top))] translate-y-1':
              placement.startsWith('top'),
          }
        )}
      >
        {isComboBox && (
          <div className="min-w-29 shrink-0">
            <div className="p-1.5 pb-0.5">
              <Ariakit.Combobox
                autoSelect
                placeholder="Search…"
                render={
                  <Input
                    rounding={rounding === 'none' ? 'none' : undefined}
                    // Fixes a problem with input unnecessarily stretching the popover
                    inputClass="!w-0"
                  />
                }
              />
            </div>
            <div className="to-[theme(backgroundColor.primary)]/0 absolute h-1 w-full bg-gradient-to-b from-[theme(backgroundColor.primary)]" />
          </div>
        )}
        <ListElement
          tabIndex={-1}
          className={classNames(
            'flex max-h-[500px] grow scroll-py-1.5 overflow-y-auto overscroll-contain p-1.5 outline-none',
            {
              'flex-col': orientation === 'vertical',
              'flex-row': orientation === 'horizontal',
              'pt-1': isComboBox,
            }
          )}
        >
          {hasChildren ? children : <div className="py-1 text-center text-2xs">Nothing to select</div>}
        </ListElement>
      </Ariakit.SelectPopover>
    </Portal>
  )
}
